
import { Options, Vue } from "vue-class-component";
import { defineAsyncComponent } from "vue";
import { BookingController } from "@/app/ui/controllers/BookingController";
import { PodDexController } from "@/app/ui/controllers/PodDexController";
import { CancelBooking, SttDetailRequest } from "@/data/payload/api/BookingRequest";
import { ReasonData } from "@/domain/entities/PodDex";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { CustomProcessEditAddressForm } from "@/domain/entities/CustomProcess";
import { DistrictData } from "@/domain/entities/Location";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { ShipmentBookingController } from "@/app/ui/controllers/ShipmentBookingController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import convertPhoneNumberV2 from "@/app/infrastructures/misc/common-library/ConvertPhoneNumberV2";
import OverlayPanel from "primevue/overlaypanel";
import { GTMOnProcessSttEvent } from "@/app/infrastructures/misc/gtm-event/custom-events/on-process-stt-events";
import { OptionsClass } from "@/domain/entities/MainApp";
import { ReasonListDex } from "@/data/payload/api/PodDexRequest";
import { TrackingController } from "@/app/ui/controllers/TrackingController";
import ModalBookingReverseJourney from "@/app/ui/views/shipment/booking/create-booking-v3/modal-booking-reverse-journey.vue";
import { SttFtzCipl } from "@/data/payload/api/BookingRequest"
import { Commodity } from "@/domain/entities/Commodity";
import { ShipmentBookingDetail } from "@/domain/entities/ShipmentBooking";
import { codePhoneCountry } from "../booking-utils";
import ModalBookingReverseJourneyV4 from "@/app/ui/views/shipment/booking/create-booking-v4/modal-booking-reverse-journey.vue";
import { FlagsPermissionBooking } from "@/feature-flags/flag-booking";

const EditAddress = defineAsyncComponent({
  loader: () =>
    import("@/app/ui/views/custom-process/component/update/edit-address.vue")
});

@Options({
  emits: ["update:isShow", "success"],
  components: { EditAddress, OverlayPanel, ModalBookingReverseJourney, ModalBookingReverseJourneyV4 },
  watch: {
    isShow() {
      if (
        this.props.isShow &&
        this.detailDataStt.sttLastStatus.match(/^REJECTED$/i)
      )
        this.onFindReasonRejected();
    }
  },
  props: {
    isShow: {
      default: false,
      type: Boolean
    },
    sttNumber: {
      type: String,
      default: ""
    },
    isDetail: {
      type: Boolean,
      default: false
    },
    customPath: {
      type: String,
      default: "booking"
    }
  }
})
class DetailRejectPopup extends Vue {
  props: any = this.$props;
  sttNo = "";
  error = {
    sttNo: false,
    captionError: ""
  };
  reverseJourneyBookingDetail = new ShipmentBookingDetail();

  onBlurSttNo() {
    if (this.sttNo.length < 17) {
      this.error.sttNo = true;
      this.error.captionError = this.confirmValue.errorInputNewStt;
    }
  }
  onInputSttNo() {
    if (this.sttNo.length === 17) this.checkSameStt();
  }
  onChangeSttNo() {
    this.error.sttNo = false;
    this.error.captionError = "";
  }
  checkSameStt() {
    if (this.sttNo === this.props.sttNumber) {
      this.error.sttNo = true;
      this.error.captionError = this.confirmValue.errorInputNewSttSame;
    }
    else this.getDetailSttNo()
  }
  async getDetailSttNo() {
    MainAppController.showLoading();
    try {
      const res = await TrackingController.trackStt({
        sttNo: this.sttNo,
        isGetSla: false,
        isSecondCall: false,
        cache: true,
       // archive: false
      });
      if (res) {
        if (this.isSttDetailAndCompanyNameEqual) {
          this.error.sttNo = false;
          this.error.captionError = "";
        } else {
          this.error.sttNo = true;
          this.error.captionError = this.confirmValue.errorInputNewStt;
        }
      } else {
        this.error.sttNo = true;
        this.error.captionError = this.confirmValue.errorInputNewStt;
      }
    } catch (error) {
      this.error.sttNo = true;
      this.error.captionError = this.confirmValue.errorInputNewStt;
    } finally {
      MainAppController.closeLoading();
    }
  }

  get isSttDetailAndCompanyNameEqual() {
    return (
      AccountController.accountData.account_type_detail.company_name.toUpperCase() ===
      TrackingController.trackingSttData.sttDetail.bookedName.toUpperCase()
    );
  }

  onSelectStatus(name: string, value: string) {
    if (!this.isShowInputNewStt) {
      this.error.sttNo = false;
      this.error.captionError = "";
      this.sttNo = "";
    }
    this.selectedReasonName = name;
    this.selectedReasonValue = value;
    this.openSelect = false;
  }

  get id(): number {
    return Number(this.$route.params.id);
  }

  get isAuth(): any {
    return this.$route?.meta?.isAuth;
  }

  get isAccountCountryMY() {
    return (
      AccountController.accountData.account_type_detail.countryCode?.toLowerCase() ===
      "my"
    );
  }

  async print(type: string, language = "id") {
    const book: any = await import(
      "@/app/ui/views/shipment/booking/create-booking/payment-detail/book.vue"
    );
    const account = AccountController.accountData.accountIsForeign;
    language = account || this.isAccountCountryMY ? "en" : "id";

    if (["INTERPACK", "INTERPACKMAXI"].includes(this.isGenerateNewStt ? this.formEditAddress.product : this.detailDataStt.sttProductTypeName)) {
      language = "en";
    }

    const printType: any = {
      thermal: () =>
        book.default.methods.printBasic(
          this.newSttIdGenerated,
          0,
          language,
          false,
          true,
          this.newSttGenerated
        ),
      basic: () =>
        book.default.methods.printBasic(
          this.newSttIdGenerated,
          1,
          language,
          undefined,
          undefined,
          this.newSttGenerated
        )
    };
    await printType[type]();
  }

  get confirmValue() {
    return {
      title: this.$t("booking.titleConfirmReject"),
      message: this.$t("booking.messageConfirmReject"),
      textSuccess: this.$t("booking.submitConfirmReject"),
      textCancel: this.$t("booking.cancelConfirmReject"),
      messageInputNewStt: this.$t("booking.messageInputNewStt"),
      errorInputNewStt: this.$t("booking.errorInputNewStt"),
      errorInputNewSttSame: this.$t("booking.errorInputNewSttSame"),
      custom: true
    };
  }
  get customStyleButtonNewStt() {
    return {
      title: this.$t('booking.printTitle'),
      textColor: "white",
      color: "red-lp-100",
      groupOptions: [
        {
          icon: "printer",
          label: "Print Thermal Resi",
          clickFunction: () => this.print("thermal")
        },
        {
          icon: "document-download-outline-grey",
          label: "Print Basic/Save to PDF",
          clickFunction: () => this.print("basic")
        }
      ]
    }
  };

  togglePanelPrintSuccess(event: any) {
    const refs: any = this.$refs;
    refs["op-success"]?.toggle(event);
  }

  get dataProfile() {
    return AccountController.accountData;
  }

  // handling
  confirmationCancel() {
    this.$emit("update:isShow", false);
  }

  async confirmationSubmit() {
    this.$emit("update:isShow", false);
    this.handleError();
    GTMOnProcessSttEvent("on_process_stt_cnx_confirm", {
      sttNumber: this.props.sttNumber,
      isGenerate: this.isGenerateNewStt,
      cnxReason: this.selectedReasonName
    });
    if (this.isGenerateNewStt) {
      this.$emit("update:isShow", false);
      await this.showEditAddress();
      return;
    }
    const resp = await BookingController.cancelBooking(
      new CancelBooking({
        sttNumber: this.props.sttNumber,
        reason: this.selectedReasonValue,
        sttNoReference: this.isShowInputNewStt ? this.sttNo : undefined
      })
    );
    if (resp?.success) {
      this.confirmationFinish = true;
      this.titleFinish = this.finishConfirmationValue.title;
      this.messageFinish = this.$t("booking.messageSuccessReject", {
        sttNumber: this.props.sttNumber
      });
      this.successFinish = this.finishConfirmationValue.textSuccess;
    }
  }

  handleError() {
    this.confirmationFinish = false;
  }

  // select
  openSelect = false;

  get isRejectedStatus(): boolean {
    return (
      !!this.detailDataStt.sttLastStatus.match(/^REJECTED$/i) &&
      !!this.detailDataStt.refundRemarks
    );
  }

  get rejectedReasons() {
    const list = [
      {
        name: this.$t("booking.placeholderReason"),
        value: ""
      },
      ...PodDexController.reasonList.map((reason: ReasonData) => {
        return { name: reason.reasonDescription, value: reason.reasonCode };
      })
    ];
    return list;
  }

  get isShowInputNewStt() {
    return (
      (this.selectedReasonValue === "RES59" ||
        this.selectedReasonValue === "RES58" || 
        this.selectedReasonValue === "RES71") &&
      AccountController.accountData.isPosAccount &&
      this.isLastSttStatusPUPOrBKD
    );
  }

  async onFindReasonRejected() {
    MainAppController.showLoading();
    if (this.detailDataStt.sttLastStatus.match(/^REJECTED$/i)) {
      await PodDexController.getReasonList(
        new ReasonListDex({
          statusCode: "REJECTED",
          reasonStatus: "active"
        })
      );
      const findReason = this.rejectedReasons.find(
        key =>
          key.name.toLowerCase() ===
          this.detailDataStt.refundRemarks.toLowerCase()
      );
      this.selectedReasonName = findReason ? findReason.name : "";
      this.selectedReasonValue = findReason ? findReason.value : "";

      if (!findReason) {
        ShipmentBookingController.shipmentBookingDetail.refundRemarks = "";
      }
    }
    MainAppController.closeLoading();
  }

  get isReasonAbleToGenerateNewStt() {
    const ableToGenerate =
      (this.dataProfile.isConsoleAccount ||
        this.dataProfile.isSubConsoleAccount) &&
      (this.isReasonAbleToGenerateNewSttREJECTED ||
        this.isReasonAbleToGenerateNewSttSTI) &&
      !this.detailDataStt.sttProductTypeName.match(/^JUMBOPACKH2H$/i);
    this.isGenerateNewStt = ableToGenerate || false;
    return ableToGenerate;
  }

  get isReasonAbleToGenerateNewSttREJECTED() {
    return (
      this.detailDataStt.sttLastStatus === "REJECTED"
    );
  }

  get isReasonAbleToGenerateNewSttSTI() {
    const reasonAbleGenerateNewSTT = PodDexController.reasonList.find(
      (reason: ReasonData) => reason.reasonCode === this.selectedReasonValue
    )?.reasonAbleGenerateNewSTT;
    return (
      this.detailDataStt.sttLastStatus === "STI" && reasonAbleGenerateNewSTT
    );
  }

  selectedReasonName = "";
  selectedReasonValue = "";
  isGenerateNewStt = false;

  // confirmation finish
  confirmationFinish = false;
  titleFinish = "";
  messageFinish = "";
  successFinish = "";
  get finishConfirmationValue() {
    return {
      title: this.$t("booking.titleSuccessReject"),
      textSuccess: "OK"
    };
  }

  directFinish() {
    this.selectedReasonValue = "";
    this.isGenerateNewStt = false;
    this.goBack();
  }

  closeModalSuccess() {
    this.confirmationFinish = false;
    this.selectedReasonValue = "";
    this.isGenerateNewStt = false;
    this.$emit("success");
  }

  goBack() {
    this.$router.push(`/shipment/${this.props.customPath}`);
  }

  get isDisabledButtonBatalkan() {
    const errorInputStt = this.isShowInputNewStt
      ? this.sttNo.length < 17 || this.error.sttNo
      : false;
    return errorInputStt || !this.selectedReasonValue;
  }

  get isLastSttStatusPUPOrBKD() {
    return (
      this.detailDataStt.sttLastStatus?.toUpperCase() == "PUP" ||
      this.detailDataStt.sttLastStatus?.toUpperCase() == "BKD"
    );
  }

  // booking cancel with generate stt
  get detailDataStt() {
    return this.reverseJourneyBookingDetail.sttId
      ? this.reverseJourneyBookingDetail
      : ShipmentBookingController.shipmentBookingDetail;
  }

  get isRtsOrCnx() {
    return this.detailDataStt.sttLastStatus === "RTS" || this.detailDataStt.sttLastStatus === "CNX" || this.detailDataStt.sttLastStatus !== "REROUTE";
  }

  editAddress = false;
  editAddressReverseJourney = false;
  editAddressReverseJourneyV4 = false;

  async showEditAddress() {
    await this.initDataEditAddress();
    if (
      this.detailDataStt.bookingFormVersion === "v4" &&
      FlagsPermissionBooking.permission_booking_v4_reverse_journey.isEnabled()
    ) {
      this.editAddressReverseJourneyV4 = true;
      return;
    }


    (this.detailDataStt.bookingFormVersion === "v4" || this.detailDataStt.isNewForm)
      ? this.editAddressReverseJourney = true
      : this.editAddress = true;
  }

  closeEditAddress(status: boolean) {
    this.editAddress = status;
    this.$emit("update:isShow", true);
  }

  closeEditAddressReverseJourney(status: boolean) {
    this.editAddressReverseJourney = status
    this.$emit("update:isShow", true);
  }

  formEditAddress: CustomProcessEditAddressForm = new CustomProcessEditAddressForm();
  newSttGenerated = "";
  newSttIdGenerated = 0;

  async cancelBookingWithGenerateStt(newData: CustomProcessEditAddressForm) {
    GTMOnProcessSttEvent("on_process_stt_cnx_generate_new_stt", {
      sttNumber: this.props.sttNumber,
      cnxReason: this.selectedReasonName
    });
    this.formEditAddress = newData;
    this.$emit("update:isShow", false);
    const resp = await BookingController.cancelBooking(
      new CancelBooking({
        sttNumber: this.props.sttNumber,
        reason: this.selectedReasonValue,
        isGenerateNewStt: true,
        sttNextCommodity: this.formEditAddress?.sttNextCommodity,
        sttPiecePerPack: this.formEditAddress?.sttPiecePerPack,
        sttBookedBy: this.formEditAddress?.sttBookedBy,
        sttReceiptName: this.formEditAddress?.consigneeName,
        sttReceiptPhone: this.formEditAddress.isNewForm ? this.formEditAddress.sttRecipientPhoneCode + this.formEditAddress.consigneePhone : this.formEditAddress?.consigneePhone,
        sttReceiptAddress: this.formEditAddress?.consigneeAddress,
        sttReceiptAddressType: this.formEditAddress?.addressType,
        sttDestinationDistrictId: (this.formEditAddress
          ?.destinationCity as DistrictData)?.code,
        sttDestinationCityId: (this.formEditAddress
          ?.destinationCity as DistrictData)?.cityCode,
        sttGoodsStatus: this.formEditAddress.sttGoodsStatus,
        sttFtzRecipientEmail: this.formEditAddress.sttFtzRecipientEmail,
        sttFtzIdentityNumber: this.formEditAddress.sttFtzIdentityNumber,
        sttTaxNumber: this.formEditAddress.sttTaxNumber,
        sttFtzTaxImage: this.formEditAddress.sttFtzTaxImage,
        sttFtzKtpImage: this.formEditAddress.sttFtzKtpImage,
        sttFtzAttachFiles: this.formEditAddress.sttFtzAttachFiles,
        sttFtzCipl: this.sttFtzCipl,
        sttCommodityCode: this.formEditAddress.sttCommodityCode,
        sttProductType: this.formEditAddress.product,
        sttCipl: this.formEditAddress.sttCipl,
        sttRecipientEmail: this.formEditAddress.sttRecipientEmail,
        sttDutyTaxTerms: this.formEditAddress.sttDutyTaxTerms,
        sttKtpImage: this.formEditAddress.sttKtpImage,
        sttTaxImage: this.formEditAddress.sttTaxImage,
        sttAttachFiles: this.formEditAddress.sttAttachFiles as string[],
        sttIdentityNumber: this.formEditAddress.sttIdentityNumber,
        sttInterTaxNumber: this.formEditAddress.sttInterTaxNumber,
        postalCodeDestination: this.formEditAddress.postalCode,
        bookingFormVersion: this.formEditAddress.bookingFormVersion,
        isNewForm: this.formEditAddress.isNewForm
      })
    );
    if (resp?.success) {
      this.newSttGenerated = resp.data.stt_success_ref_no[0].split(",")[1];
      this.newSttIdGenerated = resp.data.stt_success_ref_no[0].split(",")[2];
      this.confirmationFinish = true;
      this.titleFinish = this.$t('booking.successCancelBook');
      this.messageFinish = `STT <strong>${this.props.sttNumber}</strong> ${this.$t('booking.successCancelBookDesc')} <strong>${this.newSttGenerated}</strong>`;
      this.successFinish = "";
      this.confirmationCancel();
    }
  }

  get sttFtzCipl(): SttFtzCipl[] {
    return this.formEditAddress.sttFtzCipl;
  }

  get maskingProductNameForCNX() {
    return this.detailDataStt.sttProductTypeName.match(
      /^ONEPACK$|^BOSSPACK$|^REGPACK|^VIPPACK$|^INTERPACK|^INTERPACKMAXI$/gi
    )
      ? "REGPACK"
      : this.detailDataStt.sttProductTypeName;
  }

  async initDataEditAddress() {
    MainAppController.showLoading();
    const response = await ShipmentBookingController.getBookingDetailReverseJourney(
      new SttDetailRequest({
        id: this.detailDataStt.sttId,
        sttNo: this.detailDataStt.sttNo,
        isAuth: this.isAuth,
        pageType: "reverse_journey",
        customSuffix: "custom/",
        customStatus: "CNX"
      })
    );
    this.reverseJourneyBookingDetail = response;
    const districtData: DistrictData[] = await LocationController.getDistrictList({ search: this.detailDataStt.sttDestinationDistrictName, status: "", page: 1, limit: 10}
    );
    const isFtzLocation = !!AccountController.accountData.account_location.city_free_trade_zone.match(
      /yes/gi
    );
    const isFtz = [
      isFtzLocation,
      !!this.detailDataStt?.sttPiecePerPack,
      !!this.detailDataStt?.sttNextCommodity,
      !!this.detailDataStt.sttFtzCipl?.length
    ].includes(true);

    this.mapFtzAttachments();

    this.formEditAddress = new CustomProcessEditAddressForm({
      isFtz,
      isFtzEditable:
        isFtzLocation || !!this.detailDataStt.sttOriginCityId.match(/^BTH$/gi),
      sttNumber: this.props.sttNumber,
      sttPiecePerPack: this.detailDataStt?.sttPiecePerPack,
      sttNextCommodity:
        this.detailDataStt?.sttNextCommodity || (isFtz ? 0 : ""),
      product: this.maskingProductNameForCNX,
      addressType: this.detailDataStt.sttRecipientAddressType,
      destinationCity: new DistrictData({
        isCod: districtData[0].isCod.toLowerCase(),
        name: this.detailDataStt.sttOriginAddress,
        cityName: this.detailDataStt.sttOriginCityName,
        code: this.detailDataStt.sttOriginDistrictId,
        id: this.detailDataStt.sttOriginDistrictId as any,
        cityCode: this.detailDataStt.sttOriginCityId
      }),
      sttIsCod: this.detailDataStt.sttCod.toLowerCase(),
      sttIsDfod: this.detailDataStt.sttIsDfod,
      sttIsDo: this.detailDataStt.sttClient.clientIsDo,
      senderName: this.detailDataStt.sttRecipientName,
      senderAddress: this.detailDataStt.sttRecipientAddress,
      senderPhone: this.detailDataStt.sttRecipientPhone.includes("*")
        ? this.detailDataStt.sttRecipientPhone
        : convertPhoneNumberV2(this.detailDataStt.sttRecipientPhone),
      consigneePhone: this.detailDataStt.sttSenderPhone.includes("*")
        ? this.detailDataStt.sttSenderPhone
        : convertPhoneNumberV2(this.detailDataStt.sttSenderPhone),
      consigneeName: this.detailDataStt.sttSenderName,
      consigneeAddress: this.detailDataStt.sttSenderAddress,
      originCity: new DistrictData({
        name: this.detailDataStt.sttDestinationDistrictName,
        cityName: this.detailDataStt.sttDestinationCityName,
        code: this.detailDataStt.sttDestinationDistrictId
      }),
      commodity: new Commodity({
        // eslint-disable-next-line @typescript-eslint/camelcase
        commodity_id: this.detailDataStt.sttCommodityId,
      }),
      sttFtzCipl: this.detailDataStt.sttFtzCipl,
      sttFtzAttachFiles: this.detailDataStt.sttFtzAttachFiles,
      sttFtzIdentityNumber: this.detailDataStt.sttFtzIdentityNumber,
      sttFtzKtpImage: this.detailDataStt.sttFtzKtpImage,
      sttFtzRecipientEmail: this.detailDataStt.sttFtzRecipientEmail,
      sttFtzTaxImage: this.detailDataStt.sttFtzTaxImage,
      sttTaxNumber: this.detailDataStt.sttTaxNumber,
      sttSenderStatus: this.detailDataStt.sttSenderStatus,
      sttPieces: this.detailDataStt.SttPieces,
      sttGoodsEstimatePrice: this.detailDataStt.sttGoodsEstimatePrice,
      sttBookedForType: this.detailDataStt.sttBookedForType,
      sttSenderPhoneCode: this.detailDataStt.sttRecipientPhoneCode,
      sttRecipientPhoneCode: this.detailDataStt.sttSenderPhoneCode,
      postalCode: this.detailDataStt.postalCodeDestination,
      sttBookedFor: this.detailDataStt.sttBookedFor,
      insurance: this.detailDataStt.sttInsuranceName.toLowerCase().includes("basic") ? "basic" : "free",
      shipmentId: this.detailDataStt.sttShipmentId, sttSource: this.detailDataStt.sttSource,
      bookingFormVersion: this.detailDataStt.bookingFormVersion,
      statusStt: "CNX"
    });

    if (this.detailDataStt.bookingFormVersion === "v4") {
      this.formEditAddress.sttFtzCipl = [];
      this.formEditAddress.sttFtzAttachFiles = [];
      this.formEditAddress.sttFtzIdentityNumber = "";
      this.formEditAddress.sttFtzKtpImage = "";
      this.formEditAddress.sttFtzRecipientEmail = "";
      this.formEditAddress.sttFtzTaxImage = "";
      this.formEditAddress.sttTaxNumber = "";
      this.formEditAddress.sttCipl = [];
      this.formEditAddress.sttRecipientEmail = "";
      this.formEditAddress.sttDutyTaxTerms = "";
      this.formEditAddress.sttKtpImage = "";
      this.formEditAddress.sttTaxImage = "";
      this.formEditAddress.sttAttachFiles = [];
      this.formEditAddress.sttIdentityNumber = "";
      this.formEditAddress.sttInterTaxNumber = "";
    }
    MainAppController.closeLoading();
  }

  mapFtzAttachments() {
    if (!this.detailDataStt.sttFtzAttachFiles.length) {
      this.detailDataStt.sttFtzAttachFiles = ["", ""];
    }

    if (this.detailDataStt.sttFtzAttachFiles.length == 1) this.detailDataStt.sttFtzAttachFiles.push("");
  }
}
export default DetailRejectPopup
